<template>
	<div class="contain pad-x pt-32 pb-72 lg-pb-96 bg-gradient">
		<div class="mb-28">
			<router-link :to="backLink" class="inline-flex align-center gap-8">
				<svg class="block" xmlns="http://www.w3.org/2000/svg" width="6" height="10" fill="none" viewBox="0 0 6 10">
					<g stroke="#004ADB" stroke-linecap="round" stroke-width="1.5" clip-path="url(#clip0_5795_139780)"><path d="M5 8.5 1.5 5M1.5 5 5 1.5"/></g><defs><clipPath id="clip0_5795_139780"><path fill="#fff" d="M0 .5h6v9H0z"/></clipPath></defs>
				</svg>
				Back to {{ previousRoute == 'job' ? 'Job' : 'My Messages' }}
			</router-link>
		</div>
		<div class="lg-flex-row gap-32 thread-layout">
			<main style="flex: 1 1 878px;">
				<div class="card-v2">
					<template v-if="job && chat">
						<ChatMessages :messages="chatMessages" :profiles="profiles" />
						<ChatMessageForm :job="job.id" :chat="chat.id" :recipient="counterpartyProfile" class="mt-16" @sent="reloadMessages"/>
					</template>
					<template v-else>
						<div class="grid gap-24">
							<div v-for="i in 6" :key="`skeleton${i}`" :class="i % 2 !== 0 ? 'flex': 'flex-row-reverse align-center'" class="gap-12">
								<div v-if="i % 2 !== 0" class="skeleton square-40 round"></div>
								<div class="skeleton pt-32" style="width:500px;"></div>
							</div>
						</div>
					</template>
				</div>
			</main>
			<aside class="mt-24 lg-mt-0 lg-flex-35" style="flex: 1 1 480px;">
				<div class="card-v2">
					<template v-if="job && chat">
						<div class="flex-split gap-16">
							<div class="fs-22 lh-24 color-violet fw-700">{{ job.event.name }}</div>
							<router-link :to="{ name: $routes.JobDetails.name, params: { jobId: job.id } }" class="fs-12 no-shrink">View Job Page</router-link>
						</div>
						<div class="grid gap-12 mt-24">
							<div v-if="!isCreator" class="flex gap-8">
								<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20">
									<path stroke="#140047" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10 5.625v1.25M10 13.125v1.25"/><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10 17.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z"/><path stroke="#140047" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8.125 13.125h2.813a1.563 1.563 0 0 0 0-3.125H9.062a1.562 1.562 0 1 1 0-3.125h2.813"/>
								</svg>
								<div v-if="candidateStatus" class="fs-14 lh-14">
									{{ getRoleFromId(job.positionId).name }} - ${{ candidateStatus.ratePerHour }} per hour
								</div>
							</div>
							<div class="flex gap-8">
								<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20">
									<path fill="#004ADB" d="M16.68 8.716a.75.75 0 0 0 1.473-.29l-1.472.29Zm-7.8 9.527a.75.75 0 0 0 .207-1.486l-.207 1.486Zm8.294-8.963a.75.75 0 0 0 .485-1.419l-.485 1.42Zm-1.046-1.942a.75.75 0 0 0-.485 1.419l.485-1.42Zm.742.719a.75.75 0 1 0 1.093 1.028L16.87 8.057Zm2.176-.124a.75.75 0 1 0-1.092-1.028l1.092 1.028Zm-.893.493c-.75-3.805-4.1-6.676-8.124-6.676v1.5a6.784 6.784 0 0 1 6.652 5.466l1.471-.29ZM10.028 1.75c-4.573 0-8.279 3.71-8.279 8.286h1.5a6.783 6.783 0 0 1 6.78-6.786v-1.5ZM1.75 10.036c0 4.186 3.1 7.647 7.13 8.207l.207-1.486a6.785 6.785 0 0 1-5.837-6.72h-1.5Zm15.91-2.175-1.532-.523-.485 1.419 1.531.523.485-1.419Zm.303 1.224 1.083-1.152-1.092-1.028-1.084 1.152 1.093 1.028Z"/><path stroke="#272360" stroke-dasharray="1.5 3.25" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.45 10.712a7.671 7.671 0 0 1-6.427 6.696"/><path stroke="#272360" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.833 5.167v4.995l2.334 2.338"/>
								</svg>
								<div class="fs-14 lh-14">
									{{ formatEventDate(job.event) }}
									<template v-if="formatEventTime(job.event)">
										<span class="op-5" style="margin:0 3px;"> | </span>{{ formatEventTime(job.event) }}
									</template>
								</div>
							</div>
							<div class="flex gap-8">
								<svg class="block no-shrink" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20">
									<path stroke="#140047" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.375 18.125h11.25"/><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10 10.625a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.25 8.125c0 5.625-6.25 10-6.25 10s-6.25-4.375-6.25-10a6.25 6.25 0 0 1 12.5 0v0Z"/>
								</svg>
								<div class="fs-14 lh-14">{{ location }}</div>
							</div>
						</div>
					</template>
					<template v-else>
						<div class="flex-split">
							<div class="skeleton pt-16" style="width:170px"></div>
							<div class="skeleton pt-16" style="width:75px"></div>
						</div>
						<div class="mt-24 grid gap-12">
							<div class="skeleton pt-16" style="width:250px;"></div>
							<div class="skeleton pt-16" style="width:250px;"></div>
						</div>
					</template>
				</div>


				<div class="card-v2 mt-24">
					<template v-if="counterpartyProfile">
						<div class="flex-split gap-16">
							<div class="fs-18 lh-20 color-violet fw-700">{{ counterpartyHeading }}</div>
							<router-link :to="{ name: $routes.Profile.name, params: { id: counterpartyProfile.id } }" class="fs-12 no-shrink">View Profile</router-link>
						</div>
						<div class="mt-24 flex-top gap-24">
							<ProfileAvatar :profile="counterpartyProfile" :context="isCreator ? '' : 'studio'" size="80" class="square-80" />
							<div>
								<div v-if="isCreator">
									<div class="flex wrap gap-8">
										<div class="badge badge-green no-shrink">{{ getRoleFromId(job.positionId).name }}</div>
										<div class="fs-12 lh-16 fw-500 color-violet">${{ counterpartyQueue.ratePerHour }} per hour</div>
									</div>
									<div class="fs-18 lh-20 color-fiolet fw-700 mt-8">
										{{ counterpartyProfile.name }}
									</div>
								</div>
								<div v-else>
									<div v-if="counterpartyProfile.studioName" class="mb-4 fs-12 lh-16 color-violet fw-500">
										{{ counterpartyProfile.firstName }} {{ counterpartyProfile.lastName.slice(0, 1) }}.
									</div>
									<div class="fs-18 lh-20 color-fiolet fw-700">
										{{ counterpartyProfile.studioName ? counterpartyProfile.studioName : counterpartyProfile.firstName + ' ' + counterpartyProfile.lastName }}
									</div>
								</div>
								<Rating class="mt-8" :value="counterpartyProfile.ratingOverall" :total="counterpartyProfile.reviewCount" style="padding-bottom:3px;"/>
								<div class="flex-col gap-8 mt-8 fs-14 lh-14">
									<a class="fw-400 color-gray" :href="`mailto:${counterpartyProfile.email}`">{{ counterpartyProfile.email }}</a>
									<a v-if="counterpartyProfile.phone" class="fw-400 color-gray" :href="`tel:${counterpartyProfile.phone.replace(/[^\d]/g, '')}`">{{ counterpartyProfile.phone }}</a>
								</div>
							</div>
						</div>
					</template>
					<template v-else>
						<div class="flex-split">
							<div class="skeleton pt-16" style="width:170px"></div>
							<div class="skeleton pt-16" style="width:75px"></div>
						</div>
						<div class="mt-24 flex gap-24">
							<div class="skeleton square-80 round"></div>
							<div class="grid gap-12">
								<div class="skeleton pt-16" style="width: 130px;"></div>
								<div class="skeleton pt-16" style="width: 200px;"></div>
							</div>
						</div>
					</template>
				</div>

			</aside>
		</div>
	</div>
</template>

<script>
import helpers from '@/mixins/helpers';
import { formatEventDate, formatEventTime } from '@/helpers/format';
import ChatMessages from '@/components/chats/ChatMessages';
import ChatMessageForm from '@/components/chats/ChatMessageForm';

export default {
	name: 'MessageThread',
	components: { ChatMessageForm, ChatMessages },
	mixins: [helpers],
	computed: {
		previousRoute() {
			return this.$store.getters['cache/previousRoute'];
		},
		backLink() {
			if (this.previousRoute == 'job' && this.job) {
				return { name: this.$routes.JobDetails.name, params: { jobId: this.job.id } };
			}

			return { name: this.$routes.Messages.name };
		},
		job() {
			return this.$store.getters['jobs/job'];
		},
		chat() {
			return this.$store.getters['jobs/chat'];
		},
		chatMessages: {
			get() {
				return this.$store.getters['jobs/chatMessages'];
			},
			set(messages) {
				this.$store.commit('jobs/setChatMessages', messages);
			},
		},
		queue() {
			return this.$store.getters['jobs/queue'];
		},
		isCreator() {
			return this.$store.getters['jobs/isCreator'];
		},
		candidateUser() {
			return this.$store.getters['jobs/candidateUser'];
		},
		queueStatus() {
			return this.$store.getters['jobs/queueStatus'];
		},
		candidateStatus() {
			return this.$store.getters['jobs/candidateStatus'];
		},
		counterpartyId() {
			return this.$store.getters['jobs/chatRecipientUserId'];
		},
		counterpartyProfile() {
			const recipientId = this.counterpartyId;
			return (recipientId && this.profiles.find(({ userId }) => userId === recipientId)) || null;
		},
		counterpartyQueue() {
			if (!this.queue) return;
			const arr =  this.queue.candidates.filter(obj => obj.userId == this.counterpartyId);

			// If person is in queue multiple times, use most recent one
			return arr.length ? arr[arr.length - 1] : null;
		},
		counterpartyHeading() {
			if (!this.isCreator) return 'Studio';
			let out;
			switch (this.counterpartyQueue.status) {
				case 'accepted':
					out = 'Accepted Candidate'
					break;
				case 'awaitingResponse':
					out = 'Current Candidate';
					break;
				case 'declined':
					out = 'Declined Candidate';
					break;
				default:
					out = 'Candidate';
					break;
			}

			return out;
		},
		profiles() {
			return this.$store.getters['jobs/profiles'];
		},
		location() {
			if (!this.job) return;
			const location = this.job.event.locations[0];
			return location.address;
		}
	},
	methods: {
		formatEventDate,
		formatEventTime,
		async load() {
			const { params } = this.$route;
			this.$store.commit('jobs/setShowChat', true);
			// User is redirected in here to dashboard if chat request returns 403
			await this.$store.dispatch('jobs/loadJobView', { ...params, force: true });
		},
		async reloadMessages() {
			const payload = { chatId: this.chat.id, };
			this.chatMessages = await this.$store.dispatch('chats/getChatMessages', payload);
		},
	},
	created() {
		this.load();
	}
}
</script>